/**
 * Header Inline Search with Predictive Search
 * Supports multiple search instances on the same page
 */
class PredictiveSearch {
  constructor(formElement) {
    this.searchForm = formElement;
    this.searchInput = this.searchForm.querySelector('.Header__SearchInput');
    this.searchResults = this.searchForm.querySelector('.Header__SearchResults');

    if (!this.searchInput || !this.searchResults) return;

    this.currentRequest = null;
    this.currentSearchTerm = '';
    this.isOpen = false;

    this._attachListeners();
  }

  _attachListeners() {
    // Debounced input listener
    this.searchInput.addEventListener('input', this._debounce(this._onInput.bind(this), 300));

    // Close results when clicking outside (use event delegation)
    this._clickOutsideHandler = (event) => {
      if (!this.searchForm.contains(event.target)) {
        this._closeResults();
      }
    };

    // Close results on ESC key
    this._escKeyHandler = (event) => {
      if (event.key === 'Escape') {
        this._closeResults();
        this.searchInput.blur();
      }
    };

    this.searchInput.addEventListener('keydown', this._escKeyHandler);

    // Handle form submission
    this._submitHandler = (event) => {
      if (!this.searchInput.value.trim()) {
        event.preventDefault();
      }
    };

    this.searchForm.addEventListener('submit', this._submitHandler);

    // Add click outside listener when results open
    this.searchInput.addEventListener('focus', () => {
      if (this.currentSearchTerm && this.isOpen) {
        this._openResults();
      }
      document.addEventListener('click', this._clickOutsideHandler);
    });

    this.searchInput.addEventListener('blur', () => {
      // Delay to allow clicking on results
      setTimeout(() => {
        if (!this.searchForm.contains(document.activeElement)) {
          document.removeEventListener('click', this._clickOutsideHandler);
        }
      }, 200);
    });
  }

  _onInput(event) {
    const searchTerm = event.target.value.trim();

    if (searchTerm.length < 2) {
      this._closeResults();
      return;
    }

    if (searchTerm === this.currentSearchTerm) {
      return;
    }

    this.currentSearchTerm = searchTerm;
    this._performSearch(searchTerm);
  }

  _performSearch(searchTerm) {
    // Cancel previous request
    if (this.currentRequest) {
      this.currentRequest.abort();
    }

    const controller = new AbortController();
    this.currentRequest = controller;

    const url = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor`;

    fetch(url, { signal: controller.signal })
      .then(response => response.json())
      .then(data => {
        this._renderResults(data, searchTerm);
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          console.error('Search error:', error);
        }
      });
  }

  _renderResults(data, searchTerm) {
    const products = data.resources.results.products || [];

    if (products.length === 0) {
      this.searchResults.innerHTML = `
        <div class="Header__SearchNoResults">
          <p>No products found for "${this._escapeHtml(searchTerm)}"</p>
        </div>
      `;
    } else {
      let resultsHTML = '<div class="Header__SearchResultsList">';

      products.forEach(product => {
        //const price = product.price ? (product.price / 100).toFixed(2) : '';
        const price = product.price ? parseFloat(product.price).toLocaleString('en-IN') : '';
        const image = product.image || '';

        resultsHTML += `
          <a href="${product.url}" class="Header__SearchResultItem">
            ${image ? `<img src="${image}" alt="${this._escapeHtml(product.title)}" class="Header__SearchResultImage" loading="lazy">` : ''}
            <div class="Header__SearchResultInfo">
              <div class="Header__SearchResultTitle">${this._escapeHtml(product.title)}</div>
              ${price ? `<div class="Header__SearchResultPrice">${window.Shopify.currency.active} ${price}</div>` : ''}
            </div>
          </a>
        `;
      });

      resultsHTML += `
        <a href="${window.Shopify.routes.root}search?q=${encodeURIComponent(searchTerm)}&type=product" class="Header__SearchViewAll">
          View all results
        </a>
      </div>`;

      this.searchResults.innerHTML = resultsHTML;
    }

    this._openResults();
  }

  _openResults() {
    this.searchResults.setAttribute('aria-hidden', 'false');
    this.searchResults.classList.add('is-visible');
    this.isOpen = true;
  }

  _closeResults() {
    this.searchResults.setAttribute('aria-hidden', 'true');
    this.searchResults.classList.remove('is-visible');
    this.isOpen = false;
  }

  _escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  _debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Public method to destroy instance
  destroy() {
    this.searchInput.removeEventListener('keydown', this._escKeyHandler);
    this.searchForm.removeEventListener('submit', this._submitHandler);
    document.removeEventListener('click', this._clickOutsideHandler);

    if (this.currentRequest) {
      this.currentRequest.abort();
    }
  }
}

/**
 * Typing Effect for Search Placeholder
 */
class TypingEffect {
  constructor(inputElement, words, options = {}) {
    this.input = inputElement;
    this.words = words;
    this.staticText = options.staticText || "Search for ";
    this.typingSpeed = options.typingSpeed || 100;
    this.backspacingSpeed = options.backspacingSpeed || 60;
    this.pauseBetweenWords = options.pauseBetweenWords || 1500;

    this.wordIndex = 0;
    this.charIndex = 0;
    this.isDeleting = false;
    this.isActive = true;
    this.timeoutId = null;

    // Save original placeholder
    this.originalPlaceholder = this.input.getAttribute('placeholder') || '';

    this._attachListeners();
    this._start();
  }

  _attachListeners() {
    // Stop typing effect when user focuses on input
    this.input.addEventListener('focus', () => {
      this._stop();
      this.input.setAttribute('placeholder', this.originalPlaceholder);
    });

    // Resume typing effect when input is empty and blurred
    this.input.addEventListener('blur', () => {
      if (!this.input.value) {
        this._start();
      }
    });

    // Stop typing effect when user starts typing
    this.input.addEventListener('input', () => {
      if (this.input.value) {
        this._stop();
      }
    });
  }

  _start() {
    this.isActive = true;
    this._type();
  }

  _stop() {
    this.isActive = false;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  _type() {
    if (!this.isActive) return;

    const currentWord = this.words[this.wordIndex];

    if (!this.isDeleting) {
      // Typing - THAY ĐỔI PLACEHOLDER
      this.input.setAttribute('placeholder', this.staticText + currentWord.substring(0, this.charIndex + 1));
      this.charIndex++;

      if (this.charIndex === currentWord.length) {
        // Pause before deleting
        this.timeoutId = setTimeout(() => {
          this.isDeleting = true;
          this._type();
        }, this.pauseBetweenWords);
        return;
      }
    } else {
      // Deleting - THAY ĐỔI PLACEHOLDER
      this.input.setAttribute('placeholder', this.staticText + currentWord.substring(0, this.charIndex - 1));
      this.charIndex--;

      if (this.charIndex === 0) {
        this.isDeleting = false;
        this.wordIndex = (this.wordIndex + 1) % this.words.length;
      }
    }

    this.timeoutId = setTimeout(() => this._type(), this.isDeleting ? this.backspacingSpeed : this.typingSpeed);
  }

  destroy() {
    this._stop();
    this.input.setAttribute('placeholder', this.originalPlaceholder);
  }
}

/**
 * Search Manager - Manages all search instances
 */
class SearchManager {
  constructor() {
    this.searchInstances = [];
    this.typingInstances = [];
    this.init();
  }

  init() {
    // Find all search forms that should have predictive search
    const searchForms = document.querySelectorAll('.Header__SearchForm');

    searchForms.forEach(form => {
      // Initialize predictive search
      const searchInstance = new PredictiveSearch(form);
      this.searchInstances.push(searchInstance);

      
      // Initialize typing effect for the input
      const inputElement = form.querySelector('.Header__SearchInput');
      if (inputElement) {
        const typingInstance = new TypingEffect(inputElement, ['lipgloss', 'nails', 'foundation'], {
          staticText: 'Search for ',
          typingSpeed: 100,
          backspacingSpeed: 60,
          pauseBetweenWords: 1500
        });
        this.typingInstances.push(typingInstance);
      }

    });
  }

  destroyAll() {
    this.searchInstances.forEach(instance => instance.destroy());
    this.typingInstances.forEach(instance => instance.destroy());
    this.searchInstances = [];
    this.typingInstances = [];
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.searchManager = new SearchManager();
  });
} else {
  window.searchManager = new SearchManager();
}
